import { useServerLog } from '@/api'
import { useAppSelector } from '@/app'
import { onlineManager } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { selectors } from './coreSlice'
import {
  shouldProbeForOnline,
  shouldProbeOnOfflineTransition,
} from './onlineRecovery'

/** Abort the probe if no response within this long (treat as offline). */
const PROBE_TIMEOUT_MS = 5_000
/** Minimum gap between probes, so the paired resume events fire it only once. */
const PROBE_DEBOUNCE_MS = 3_000

const DOCUMENT_EVENTS = ['visibilitychange', 'resume'] as const
const WINDOW_EVENTS = ['pageshow', 'online'] as const

/**
 * Recovers the live-update loop after a freeze/resume that left react-query
 * stuck offline (see `onlineRecovery.ts` for the why).
 *
 * Two triggers, both leading to the same probe: (1) a resume/visibility/online
 * lifecycle event while react-query already reports offline, and (2) react-query
 * transitioning to offline while the page is visible (catches the post-resume
 * case where the offline flip lags the lifecycle events). The probe is a
 * lightweight `no-cors` HEAD to the backend: any reachable response confirms
 * connectivity and we call `onlineManager.setOnline(true)` to unpause the
 * queries. A genuine network failure leaves react-query paused — so when truly
 * offline this adds no request spam. Deliberately separate from the
 * observation-only `useUpdateHealthMonitor`, which must take no corrective
 * action.
 *
 * Mount once in the authenticated shell.
 */
export const useOnlineRecovery = () => {
  const baseUrl = useAppSelector(selectors.selectBackend).baseUrl
  const serverLog = useServerLog()

  const baseUrlRef = useRef(baseUrl)
  baseUrlRef.current = baseUrl
  const serverLogRef = useRef(serverLog)
  serverLogRef.current = serverLog
  const lastProbeRef = useRef(0)
  const probingRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const probe = async () => {
      const url = baseUrlRef.current
      if (!url || probingRef.current) {
        return
      }
      const now = Date.now()
      if (now - lastProbeRef.current < PROBE_DEBOUNCE_MS) {
        return
      }
      lastProbeRef.current = now
      probingRef.current = true
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
      try {
        // `no-cors` HEAD resolves (opaque) for any reachable server and rejects
        // only on a genuine network failure — the connectivity signal we want,
        // with no CORS/auth complications.
        await fetch(url, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        })
        if (!onlineManager.isOnline()) {
          // The browser's `online` event was missed across freeze/resume but we
          // have connectivity — unpause react-query so polling resumes.
          onlineManager.setOnline(true)
          try {
            serverLogRef.current.warn(
              '[online-recovery] connectivity confirmed after resume; resumed react-query (was offline)'
            )
          } catch {
            // Never let diagnostics logging throw into the app.
          }
        }
      } catch {
        // Genuinely offline (or probe aborted) — leave react-query paused.
      } finally {
        clearTimeout(timeout)
        probingRef.current = false
      }
    }

    const handler = (event: Event) => {
      const visibilityState =
        typeof document === 'undefined' ? 'unknown' : document.visibilityState
      if (
        shouldProbeForOnline(
          event.type,
          visibilityState,
          onlineManager.isOnline()
        )
      ) {
        void probe()
      }
    }

    DOCUMENT_EVENTS.forEach(type =>
      document.addEventListener(type, handler, { passive: true })
    )
    WINDOW_EVENTS.forEach(type =>
      window.addEventListener(type, handler, { passive: true })
    )

    // The lifecycle events above can fire while onlineManager still reports
    // online, with the offline flip landing a few hundred ms later and no
    // further event to re-trigger the probe. React to the offline transition
    // itself (while visible) so that lagging flip can't leave us stuck paused.
    const unsubscribe = onlineManager.subscribe((online: boolean) => {
      const visibilityState =
        typeof document === 'undefined' ? 'unknown' : document.visibilityState
      if (shouldProbeOnOfflineTransition(online, visibilityState)) {
        void probe()
      }
    })

    return () => {
      DOCUMENT_EVENTS.forEach(type =>
        document.removeEventListener(type, handler)
      )
      WINDOW_EVENTS.forEach(type => window.removeEventListener(type, handler))
      unsubscribe()
    }
    // Listeners close over stable refs only.
  }, [])
}
